/**
 * Typed API client for the collector's read-only metrics endpoints.
 *
 * Every response is parsed through a zod schema. That is deliberate and it is
 * the main reason this app is TypeScript: each panel is a numeric contract, and
 * a renamed or removed field must fail loudly at the boundary rather than
 * render `NaN` or `undefined` on a wall display that someone is trusting.
 */
import { z } from 'zod'

export class ApiError extends Error {
  constructor(readonly status: number, readonly path: string, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Thrown when the server answered but the shape is not what we contracted for. */
export class ContractError extends Error {
  constructor(readonly path: string, readonly issues: string) {
    super(`${path}: response did not match its schema — ${issues}`)
    this.name = 'ContractError'
  }
}

/** The message an `ApiError` carries for a non-2xx response.
 *
 *  FastAPI's `HTTPException` renders as `{"detail": "..."}`, and every route
 *  on this board that can fail with a message a user could act on (all four
 *  /admin/mapper-keys routes, at minimum) raises exactly that shape. Reading
 *  it here is what turns "name must match ^[a-z0-9-]{1,64}$" or "an active
 *  key named 'x' exists — rotate it instead, or revoke it first" into the
 *  KEYS panel's error text instead of a bare "HTTP 422"/"HTTP 409" that tells
 *  the operator nothing they can act on.
 *
 *  This runs on every failing request across the whole app, so it must never
 *  itself throw: a body that is missing, not JSON, or JSON without a string
 *  `detail` falls back to the plain status line exactly as before. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json()
    if (body && typeof body === 'object' && 'detail' in body) {
      const detail = (body as { detail: unknown }).detail
      if (typeof detail === 'string' && detail.length > 0) return detail
    }
  } catch {
    // No body, not JSON, or a shape this does not understand — the response
    // stream is already spent either way, so there is nothing left to fall
    // back to but the status line below.
  }
  return `HTTP ${res.status}`
}

export async function get<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  signal?: AbortSignal,
): Promise<z.infer<T>> {
  const res = await fetch(path, {
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('dash:unauthorized'))
    throw new ApiError(res.status, path, await errorMessage(res))
  }
  const parsed = schema.safeParse(await res.json())
  if (!parsed.success) {
    throw new ContractError(
      path,
      parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
    )
  }
  return parsed.data
}

/** Same contract as `get`, POST method — for actions on this board that
 *  trigger server-side work rather than reading it (the tier-activity
 *  refresh; the KEYS panel's create/revoke/rotate). A non-2xx status
 *  (including 409 "already running"/"name taken"/"already revoked") still
 *  raises `ApiError` here, exactly like `get` — callers that need to treat a
 *  particular status as non-fatal (see `useTierActivityRefresh` in
 *  api/queries.ts) inspect `error.status` themselves rather than this
 *  function silently swallowing it, so every other caller still gets a loud
 *  failure by default.
 *
 *  `body`, when passed, is JSON-serialised and sent with a matching
 *  Content-Type; omitted (`undefined`) it sends no body at all, unchanged
 *  from this function's original no-body-ever behaviour — the tier-activity
 *  refresh call keeps working with no changes at its call site.
 *
 *  NOTE the parameter order: the third positional argument is now `body`,
 *  not `signal` — a future caller passing an `AbortSignal` as the third
 *  argument (this function's old shape) compiles cleanly and silently sends
 *  the signal object itself as the JSON body instead of aborting anything.
 *  Pass an abort signal fourth, or as `undefined, signal` if there is no body. */
export async function post<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  body?: unknown,
  signal?: AbortSignal,
): Promise<z.infer<T>> {
  const hasBody = body !== undefined
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(hasBody ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('dash:unauthorized'))
    throw new ApiError(res.status, path, await errorMessage(res))
  }
  const parsed = schema.safeParse(await res.json())
  if (!parsed.success) {
    throw new ContractError(
      path,
      parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
    )
  }
  return parsed.data
}
